import {Button, FormControl, InputLabel, MenuItem, Select, TextField, Chip} from '@material-ui/core';
import {DatePicker} from '@material-ui/pickers';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {
  changeAlertDescription,
  changeAlertEndDate,
  changeAlertStartDate,
  changeAlertTitle,
  clearAlert,
  getAlert,
  saveAlert,
  changeAlertApplications,
} from 'Core/ducks/alerts';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {ValidAlert} from 'Core/models/Alerts';
import {matchAsync} from 'Core/models/Async';
import {flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {DateTime} from 'luxon';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {bindActionCreators} from 'redux';
import {ReducerState} from 'rootReducer';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import {LinesOfBusiness, LineOfBusinessValue} from 'Users/models/LineOfBusiness';
import './AlertsEntry.sass';
import {mapLineOfBusinessToApplication} from 'Users/models/Application';

type AlertsEntryProps = RouteComponentProps<{alertId: string}>;

const AlertsEntry = (props: AlertsEntryProps) => {
  const dispatch = useDispatch();
  const {alert} = useSelector((state: ReducerState) => state.alerts);
  const editAlertId = O.fromNullable(props.match.params.alertId);
  const returnNullDate = (): DateTime | null => null;
  const returnUndefinedDate = (): DateTime | undefined => undefined;
  const dateOrNull = flow(O.getOrElse(returnNullDate));
  const dateOrUndefined = flow(O.getOrElse(returnUndefinedDate));
  const {getAlert: boundGetAlert} = bindActionCreators({getAlert}, dispatch);
  const operationLabel = pipe(
    editAlertId,
    O.fold(
      () => 'Add',
      () => 'Edit'
    )
  );
  useBreadcrumbs(`${operationLabel} Alert`, props.match.url);

  useEffect(() => {
    pipe(O.some(boundGetAlert), O.ap(editAlertId));

    return () => {
      dispatch(clearAlert());
    };
  }, []);

  return matchAsync(alert, {
    Conflict: error => <ErrorPage error={error} />,
    Loaded: loadedAlert => (
      <div className="alert-entry">
        <span className="alert-entry__title">
          {operationLabel}
          &nbsp;Alert
        </span>
        <p className="alert-entry__description">Please provide the alert details below.</p>
        <TextField
          className="alert-entry__field"
          onChange={event => {
            dispatch(changeAlertTitle(event.target.value));
          }}
          value={loadedAlert.title.value}
          label="Title"
          variant="outlined"
        />
        <TextField
          multiline
          rows={4}
          rowsMax={10}
          label="Description"
          className="alert-entry__field"
          variant="outlined"
          value={loadedAlert.description.value}
          onChange={event => {
            dispatch(changeAlertDescription(event.target.value));
          }}
        />
        <DatePicker
          label="Start Date"
          className="alert-entry__field"
          maxDate={dateOrUndefined(loadedAlert.endDate)}
          value={dateOrNull(loadedAlert.startDate)}
          onChange={startDate => {
            dispatch(changeAlertStartDate(startDate));
          }}
        />
        <DatePicker
          label="End Date"
          className="alert-entry__field"
          minDate={dateOrUndefined(loadedAlert.startDate)}
          value={dateOrNull(loadedAlert.endDate)}
          onChange={endDate => {
            dispatch(changeAlertEndDate(endDate));
          }}
        />
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Line of Businesses</InputLabel>
          <Select
            multiple
            fullWidth
            labelWidth={145}
            className="alert-entry__field"
            variant="outlined"
            renderValue={() =>
              loadedAlert.applications.map(
                (application, index) =>
                  application && (
                    <Chip color="primary" key={`${application.name} - ${index}`} label={application.name} />
                  )
              )
            }
            value={loadedAlert.applications.map(a => a.id)}
            onChange={(e, menuEl) => {
              if (!!loadedAlert.applications.find(a => a.id === (menuEl as any).props.value)) {
                dispatch(
                  changeAlertApplications(loadedAlert.applications.filter(a => a.id !== (menuEl as any).props.value))
                );
              } else {
                dispatch(
                  changeAlertApplications([
                    ...loadedAlert.applications,
                    {name: (menuEl as any).props.children, id: (menuEl as any).props.value},
                  ])
                );
              }
            }}
          >
            {LinesOfBusiness.map(lob => (
              <MenuItem key={lob.value} value={mapLineOfBusinessToApplication(lob.value as LineOfBusinessValue)}>
                {lob.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className="alert-entry__actions">
          <Button
            className="alert-entry__cancel-button"
            onClick={() => {
              props.history.push(CoreRouteConstants.ALERTS);
            }}
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            disabled={loadedAlert.key !== 'ValidAlert'}
            onClick={() => {
              if (loadedAlert instanceof ValidAlert) {
                dispatch(saveAlert(loadedAlert));
              }
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </div>
      </div>
    ),
    Loading: () => <Loading />,
    ServerError: error => <ErrorPage error={error} />,
    Unauthorized: error => <ErrorPage error={error} />,
  });
};

export default withRouter(AlertsEntry);
